import { useContext, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LocationOn from '@mui/icons-material/LocationOn'
import AccessTime from '@mui/icons-material/AccessTime'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined'
import BlockOutlined from '@mui/icons-material/BlockOutlined'
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined'
import { PTSControlContext } from '../PTSControl'
import { getJourneyWorkflow } from '../../journeyWorkflow'
import JourneyTimesStrip from '../../components/JourneyTimesStrip'
import API from '../../../../services/API'

const getPriorityStyles = (priority) => {
  switch (priority) {
    case 'Emergency':
      return {
        bg: 'rgba(239,68,68,0.18)',
        border: 'rgba(239,68,68,0.35)',
        color: '#FCA5A5',
      }
    case 'ASAP':
    case 'Urgent':
      return {
        bg: 'rgba(245,158,11,0.18)',
        border: 'rgba(245,158,11,0.35)',
        color: '#FCD34D',
      }
    default:
      return {
        bg: 'rgba(255,255,255,0.06)',
        border: 'rgba(255,255,255,0.08)',
        color: '#E2E8F0',
      }
  }
}

const formatJourneyTime = (journey) => {
  const ready =
    journey?.pickup?.when?.patientReadyFrom ||
    journey?.dropoff?.when?.targetArrivalTime

  return ready
    ? `${journey?.pickup?.when?.patientReadyFrom ? 'Ready From:' : journey?.dropoff?.when?.targetArrivalTime ? 'Target Arrival:' : ''} ${moment(ready).format('HH:mm')}`
    : 'No time set'
}

// Live ETA from the server for the duty's current stop. journey.eta is
// undefined for journeys that aren't the next stop, null when the vehicle has
// no fresh position. Margin: + (green) = ahead of the target time,
// − (red) = behind it.
const EtaChip = ({ eta }) => {
  if (eta === undefined) return null

  if (eta === null) {
    return (
      <Tooltip title="The vehicle hasn't reported a position in the last 5 minutes">
        <Chip
          label="No live location"
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.6)',
            backgroundColor: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 1.5,
          }}
        />
      </Tooltip>
    )
  }

  const slack = eta.slackMinutes
  const hasSlack = Number.isFinite(slack)
  const late = hasSlack && slack < 0
  const margin = hasSlack ? ` ${late ? '−' : '+'}${Math.abs(slack)}` : ''
  const updated = eta.locationUpdatedAt
    ? `position ${moment(eta.locationUpdatedAt).fromNow()}`
    : ''
  const tooltip = [
    `Estimated arrival at ${eta.leg === 'dropoff' ? 'destination' : 'pickup'}`,
    hasSlack
      ? `${Math.abs(slack)} min ${late ? 'after' : 'before'} the ${eta.leg === 'dropoff' ? 'target arrival' : 'ready from'} time`
      : null,
    updated,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Tooltip title={tooltip}>
      <Chip
        label={`ETA ${moment(eta.arrivalAt).format('HH:mm')}${margin}`}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.72rem',
          fontWeight: 700,
          borderRadius: 1.5,
          ...(hasSlack
            ? late
              ? {
                  color: '#FCA5A5',
                  backgroundColor: 'rgba(239,68,68,0.16)',
                  border: '1px solid rgba(239,68,68,0.35)',
                }
              : {
                  color: '#86EFAC',
                  backgroundColor: 'rgba(34,197,94,0.14)',
                  border: '1px solid rgba(34,197,94,0.32)',
                }
            : {
                color: '#E2E8F0',
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
              }),
        }}
      />
    </Tooltip>
  )
}

const getJourneyLocationLine = (loc) => {
  if (!loc) return 'No location'
  return [loc.name, loc.addressLine1, loc.town || loc.city, loc.postCode]
    .filter(Boolean)
    .join(', ')
}

const getJourneyWardLine = (loc) =>
  [loc?.wardDepartmentAsAddress?.name || loc?.wardName, loc?.wardLocation]
    .filter(Boolean)
    .join(' - ')

const JourneyMiniCard = ({ journey, routeIndex, journeyIndex }) => {
  const { updateJourneyStatus, selectedDuty, refreshControl } =
    useContext(PTSControlContext)
  const [exceptionConfirm, setExceptionConfirm] = useState(null)
  const [unassignConfirm, setUnassignConfirm] = useState(false)
  const [unassigning, setUnassigning] = useState(false)
  const [unassignError, setUnassignError] = useState(null)
  const priorityStyles = getPriorityStyles(journey?.priority)
  const workflow = getJourneyWorkflow(journey?.status, journey)

  const patientName =
    journey?.patient?.demographics?.firstName &&
    journey?.patient?.demographics?.lastName
      ? `${journey.patient.demographics.firstName} ${journey.patient.demographics.lastName}`
      : 'Unknown patient'

  const confirmExceptionStatus = async () => {
    if (!exceptionConfirm?.status) return
    await updateJourneyStatus?.(
      journey._id,
      exceptionConfirm.status,
      journey,
    )
    setExceptionConfirm(null)
  }

  const confirmUnassign = async () => {
    if (!selectedDuty?._id || routeIndex === undefined || journeyIndex === undefined) {
      return
    }
    setUnassigning(true)
    setUnassignError(null)
    try {
      const res = await API.post(
        `/pts/duties/${selectedDuty._id}/routes/remove-journey`,
        {
          journeyID: journey._id,
          routeIndex,
          journeyIndex,
        },
      )
      if (res.status === 200) {
        setUnassignConfirm(false)
        refreshControl?.()
      } else {
        setUnassignError(
          res.data?.error || 'Unable to remove this journey from the duty.',
        )
      }
    } catch (err) {
      setUnassignError(
        err?.response?.data?.error ||
          'Unable to remove this journey from the duty.',
      )
    } finally {
      setUnassigning(false)
    }
  }

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box minWidth={0}>
            <Typography
              sx={{
                color: '#F8FAFC',
                fontWeight: 700,
                fontSize: '0.92rem',
                lineHeight: 1.15,
              }}
            >
              {patientName}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(226,232,240,0.64)',
                fontSize: '0.76rem',
                mt: 0.2,
              }}
            >
              {journey?.journeyType || 'Journey'}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Chip
              label={journey?.priority || 'Routine'}
              size="small"
              sx={{
                height: 22,
                color: priorityStyles.color,
                backgroundColor: priorityStyles.bg,
                border: `1px solid ${priorityStyles.border}`,
                fontWeight: 700,
                borderRadius: 1.5,
              }}
            />
            <Chip
              label={workflow.stateLabel}
              size="small"
              sx={{
                height: 22,
                color: '#93C5FD',
                backgroundColor: 'rgba(59,130,246,0.16)',
                border: '1px solid rgba(59,130,246,0.3)',
                fontWeight: 700,
                borderRadius: 1.5,
              }}
            />
          </Stack>
        </Stack>

        <Stack spacing={0.75}>
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <LocationOn sx={{ fontSize: 16, color: '#93C5FD', mt: '2px' }} />
            <Box minWidth={0}>
              <Typography
                sx={{
                  color: '#E2E8F0',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                }}
              >
                Pickup
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(226,232,240,0.66)',
                  fontSize: '0.74rem',
                  lineHeight: 1.2,
                }}
              >
                {getJourneyLocationLine(journey?.pickup)}
              </Typography>
              {getJourneyWardLine(journey?.pickup) && (
                <Typography
                  sx={{
                    color: 'rgba(226,232,240,0.82)',
                    fontSize: '0.74rem',
                    lineHeight: 1.2,
                    fontWeight: 700,
                  }}
                >
                  {getJourneyWardLine(journey.pickup)}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <LocationOn sx={{ fontSize: 16, color: '#86EFAC', mt: '2px' }} />
            <Box minWidth={0}>
              <Typography
                sx={{
                  color: '#E2E8F0',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                }}
              >
                Dropoff
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(226,232,240,0.66)',
                  fontSize: '0.74rem',
                  lineHeight: 1.2,
                }}
              >
                {getJourneyLocationLine(journey?.dropoff)}
              </Typography>
              {getJourneyWardLine(journey?.dropoff) && (
                <Typography
                  sx={{
                    color: 'rgba(226,232,240,0.82)',
                    fontSize: '0.74rem',
                    lineHeight: 1.2,
                    fontWeight: 700,
                  }}
                >
                  {getJourneyWardLine(journey.dropoff)}
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AccessTime
              sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.56)' }}
            />
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.72)',
                fontSize: '0.84rem',
                fontWeight: 600,
              }}
            >
              {formatJourneyTime(journey)}
            </Typography>
            <EtaChip eta={journey?.eta} />
          </Stack>
          {journey?.booking?.bookingRef && (
            <Typography
              sx={{
                // color: 'rgba(255,255,255,0.46)',
                color: 'contrast.light',
                fontSize: '0.84rem',
                fontWeight: 700,
                ml: 'auto',
              }}
            >
              {journey.booking.bookingRef}
            </Typography>
          )}
        </Stack>

        <JourneyTimesStrip journey={journey} dark dense />

        {!workflow.isTerminal && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              pt: 0.25,
            }}
          >
            {workflow.nextStatus && (
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                onClick={() =>
                  updateJourneyStatus?.(
                    journey._id,
                    workflow.nextStatus,
                    journey,
                  )
                }
                sx={{
                  minHeight: 34,
                  minWidth: 0,
                  borderRadius: 1,
                  px: 1.25,
                  py: 0.6,
                  bgcolor: '#239AA4',
                  color: '#FFFFFF',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 900,
                  lineHeight: 1.15,
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                  },
                  '&:hover': {
                    bgcolor: '#1E8790',
                    boxShadow: 'none',
                  },
                }}
              >
                {workflow.actionLabel}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReportProblemOutlined sx={{ fontSize: 16 }} />}
              onClick={() =>
                setExceptionConfirm({
                  status: 'No Show',
                  label: 'No Show',
                  description:
                    'This will mark the patient as not present and close this journey.',
                })
              }
              sx={{
                minHeight: 34,
                minWidth: 0,
                borderRadius: 1,
                px: 1.1,
                py: 0.55,
                color: '#FCA5A5',
                borderColor: 'rgba(248,113,113,0.34)',
                bgcolor: 'rgba(248,113,113,0.06)',
                textTransform: 'none',
                fontSize: '0.76rem',
                fontWeight: 850,
                lineHeight: 1.15,
                '& .MuiButton-startIcon': {
                  mr: 0.45,
                },
                '&:hover': {
                  borderColor: 'rgba(248,113,113,0.58)',
                  bgcolor: 'rgba(248,113,113,0.12)',
                },
              }}
            >
              No Show
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BlockOutlined sx={{ fontSize: 16 }} />}
              onClick={() =>
                setExceptionConfirm({
                  status: 'Aborted',
                  label: 'Cannot complete',
                  description:
                    'This will close the journey as unable to complete.',
                })
              }
              sx={{
                minHeight: 34,
                minWidth: 0,
                borderRadius: 1,
                px: 1.1,
                py: 0.55,
                color: '#FCA5A5',
                borderColor: 'rgba(248,113,113,0.34)',
                bgcolor: 'rgba(248,113,113,0.06)',
                textTransform: 'none',
                fontSize: '0.76rem',
                fontWeight: 850,
                lineHeight: 1.15,
                '& .MuiButton-startIcon': {
                  mr: 0.45,
                },
                '&:hover': {
                  borderColor: 'rgba(248,113,113,0.58)',
                  bgcolor: 'rgba(248,113,113,0.12)',
                },
              }}
            >
              Cannot complete
            </Button>
            {routeIndex !== undefined && journeyIndex !== undefined && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonRemoveOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setUnassignConfirm(true)}
                sx={{
                  minHeight: 34,
                  minWidth: 0,
                  borderRadius: 1,
                  px: 1.1,
                  py: 0.55,
                  color: 'rgba(226,232,240,0.82)',
                  borderColor: 'rgba(226,232,240,0.28)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  textTransform: 'none',
                  fontSize: '0.76rem',
                  fontWeight: 850,
                  lineHeight: 1.15,
                  '& .MuiButton-startIcon': {
                    mr: 0.45,
                  },
                  '&:hover': {
                    borderColor: 'rgba(226,232,240,0.5)',
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                Unassign
              </Button>
            )}
          </Box>
        )}
      </Stack>
      <Dialog
        open={!!exceptionConfirm}
        onClose={() => setExceptionConfirm(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Confirm {exceptionConfirm?.label}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            {exceptionConfirm?.description} Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExceptionConfirm(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmExceptionStatus}
            variant="contained"
            color="error"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={unassignConfirm}
        onClose={() => (unassigning ? null : setUnassignConfirm(false))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Unassign journey</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            This removes {patientName}'s journey from this duty and returns it
            to the unassigned list. Are you sure?
          </Typography>
          {unassignError && (
            <Typography sx={{ color: 'error.main', mt: 1 }}>
              {unassignError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setUnassignConfirm(false)}
            color="inherit"
            disabled={unassigning}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmUnassign}
            variant="contained"
            color="error"
            disabled={unassigning}
          >
            {unassigning ? 'Removing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default JourneyMiniCard
